import {
  Icon,
  useColorScheme,
  useHover,
  useLanguage,
  useLocales,
} from "@/lib/main";
import { DrawerMenuItemActionType } from "@/lib/types/navigation/header/drawer/DrawerMenuItem.type";
import { CSSProperties } from "react";
import { useNavigate } from "react-router";

type Props = {
  drawerMenuItem: DrawerMenuItemActionType;
  level?: number;
  setIsDrawerOpen: (isDrawerOpen: boolean) => void;
  shoppingCartCount?: number;
};

export default function DrawerMenuItemAction({
  drawerMenuItem,
  level = 1,
  setIsDrawerOpen,
}: Props) {
  const navigate = useNavigate();
  const l = useLocales();
  const { colorScheme, toggleColorScheme } = useColorScheme();
  const { language, toggleLanguage } = useLanguage();
  const { isHovered, onMouseEnter, onMouseLeave } = useHover();

  const { action, actionFunction } = drawerMenuItem;

  const handleClick = () => {
    setIsDrawerOpen(false);

    if (action === "custom" && actionFunction) {
      return actionFunction();
    }

    if (action === "logout") {
      if (actionFunction) return actionFunction();
      else return navigate("/logout");
    }

    if (action === "toggleColorScheme") return toggleColorScheme();

    if (action === "toggleLanguage") return toggleLanguage();
  };

  const liStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",

    width: "100%",

    borderBottom: "var(--border-width-xs) solid var(--color-border-subtle)",
    backgroundColor: isHovered
      ? colorScheme === "dark"
        ? "var(--color-background-frame)"
        : "var(--color-background-canvas)"
      : "transparent",
  };

  const rowPaddingStart =
    level <= 1
      ? "var(--page-padding-horizontal)"
      : `calc(var(--page-padding-horizontal) + (${level - 1} * var(--spacing-2xl)))`;

  const linkStyle: CSSProperties = {
    display: "flex",
    gap: "var(--spacing-sm)",
    alignItems: "center",

    width: "100%",
    paddingTop: "var(--spacing-md)",
    paddingBottom: "var(--spacing-md)",
    paddingInlineStart: rowPaddingStart,
    paddingInlineEnd: "var(--page-padding-horizontal)",

    fontSize: "var(--font-size-drawer-item)",
  };

  const noLinkStyle: CSSProperties = {
    ...linkStyle,
    color: "var(--color-text-primary)",
  };

  const logoutStyle: CSSProperties = {
    ...linkStyle,
    color: "var(--color-text-danger)",
  };

  const buttonClassName =
    action === "toggleLanguage"
      ? language === "en"
        ? "font-color-nav arabic-font-override"
        : "font-color-nav english-font-override"
      : "font-color-nav";

  const buttonStyle: CSSProperties = {
    ...(action === "logout" ? logoutStyle : noLinkStyle),
    background: "transparent",
    border: "none",
    cursor: "pointer",
    textAlign: "start",
  };

  return (
    <li onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} style={liStyle}>
      <button
        className={buttonClassName}
        onClick={handleClick}
        style={buttonStyle}
        type="button"
      >
        <Icon
          color={drawerMenuItem.action === "logout" ? "danger" : "secondary"}
          name={drawerMenuItem.icon}
          size="md"
        />

        {l(drawerMenuItem.title)}
      </button>
    </li>
  );
}
